// Modular analytics layer for the AI Turf Designer SaaS marketing funnel ONLY.
// GA4 + Meta Pixel are loaded/fired exclusively on the marketing/demo/signup routes
// below — never on Prime Gym Turf storefront/customer routes (/, /design, /embed/*, /t/*, /admin/*).
// Designed to be portable: if the marketing site moves to its own domain, this file moves with it.
import { trackMarketing, IS_SAAS_HOST } from "./api";

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const META_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const GTM_ID = process.env.REACT_APP_GTM_ID || "";

// Routes where SaaS marketing tracking is allowed.
const TRACKED = [
  "/software", "/saas", "/pricing", "/demo", "/contact",
  "/gym-turf-designer", "/artificial-turf-designer", "/gym-flooring-software", "/turf-visualiser",
  "/signup", "/onboarding", "/billing/return", "/dashboard",
];

export function isTrackedRoute(path = window.location.pathname) {
  if (IS_SAAS_HOST && path === "/") return true; // marketing homepage on a custom SaaS domain
  return TRACKED.some((r) => path === r || path.startsWith(r + "/"));
}

// ---------------- attribution (UTM + referrer, first-touch persisted) ----------------
const ATTR_KEY = "atd_attribution";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"];

function deriveSource(ref) {
  if (!ref) return "direct";
  try {
    const h = new URL(ref).hostname.toLowerCase();
    if (h.includes("google")) return "google";
    if (h.includes("facebook") || h.includes("instagram") || h.includes("fb.")) return "meta";
    if (h.includes("bing")) return "bing";
    if (h.includes(window.location.hostname)) return "internal";
    return "referral";
  } catch { return "direct"; }
}

export function captureAttribution() {
  const sp = new URLSearchParams(window.location.search);
  const utm = {};
  UTM_KEYS.forEach((k) => { const v = sp.get(k); if (v) utm[k] = v; });
  let stored = null;
  try { stored = JSON.parse(localStorage.getItem(ATTR_KEY) || "null"); } catch { stored = null; }
  if (!stored) {
    stored = {
      utm_source: utm.utm_source || deriveSource(document.referrer),
      utm_medium: utm.utm_medium || (Object.keys(utm).length ? "cpc" : (document.referrer ? "referral" : "none")),
      utm_campaign: utm.utm_campaign || "",
      utm_content: utm.utm_content || "",
      utm_term: utm.utm_term || "",
      gclid: utm.gclid || "", fbclid: utm.fbclid || "",
      referrer: document.referrer || "",
      landing_page: window.location.pathname,
      first_seen: new Date().toISOString(),
    };
    try { localStorage.setItem(ATTR_KEY, JSON.stringify(stored)); } catch { /* ignore */ }
  } else if (Object.keys(utm).length) {
    // keep first-touch, but refresh last-touch UTMs so re-entry via a new campaign is captured
    stored = { ...stored, ...utm, last_landing: window.location.pathname };
    try { localStorage.setItem(ATTR_KEY, JSON.stringify(stored)); } catch { /* ignore */ }
  }
  return stored;
}

export function getAttribution() {
  try { return JSON.parse(localStorage.getItem(ATTR_KEY) || "null") || captureAttribution(); }
  catch { return captureAttribution(); }
}

function attrParams(a) {
  if (!a) return {};
  const out = {
    utm_source: a.utm_source, utm_medium: a.utm_medium, utm_campaign: a.utm_campaign,
    utm_content: a.utm_content, utm_term: a.utm_term,
    source: a.utm_source, medium: a.utm_medium, campaign: a.utm_campaign,
    referrer: a.referrer, landing_page: a.landing_page,
  };
  Object.keys(out).forEach((k) => { if (out[k] === undefined || out[k] === "") delete out[k]; });
  return out;
}

// ---------------- script loading (once, only on tracked routes) ----------------
let inited = false;

function loadGTM() {
  if (!GTM_ID) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
}

function loadGA4() {
  if (!GA4_ID) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { send_page_view: false });
}

function loadMeta() {
  if (window.__atd_meta_inited) return; // base pixel already loaded + initialised statically from index.html (SaaS domain)
  if (!META_ID) return;
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", META_ID);
  window.__atd_meta_inited = true;
}

export function initAnalytics() {
  if (inited || typeof window === "undefined") return;
  if (!isTrackedRoute()) return; // never initialise on PGT routes
  inited = true;
  captureAttribution();
  loadGTM();
  loadGA4();
  loadMeta();
}

// ---------------- page views (SPA-aware) ----------------
export function trackPageView(pathname = window.location.pathname) {
  if (!isTrackedRoute(pathname)) return;
  initAnalytics();
  const attr = attrParams(getAttribution());
  if (GA4_ID && window.gtag) window.gtag("event", "page_view", { page_path: pathname, page_location: window.location.href, page_title: document.title, ...attr });
  if (window.fbq) window.fbq("track", "PageView");
  if (window.dataLayer) window.dataLayer.push({ event: "page_view", page_path: pathname, ...attr });
}

// ---------------- conversion events ----------------
// Meta standard-event mapping; anything unmapped fires as a Meta custom event.
const META_MAP = {
  pricing_view: ["ViewContent", { content_name: "Pricing" }],
  contact_submitted: ["Lead", {}],
  signup_completed: ["CompleteRegistration", {}],
  checkout_started: ["InitiateCheckout", {}],
  trial_started: ["StartTrial", {}],
  subscription_started: ["Subscribe", {}],
};

export function track(name, params = {}) {
  if (!isTrackedRoute()) return; // isolation: never emit SaaS tracking (GA/Meta OR backend funnel) from PGT routes
  const attr = attrParams(getAttribution());
  const payload = { ...params, ...attr };
  // Backend marketing funnel (isolated under tenant_slug="marketing", never PGT).
  try { trackMarketing(name, payload); } catch { /* ignore */ }
  initAnalytics();
  if (GA4_ID && window.gtag) window.gtag("event", name, payload);
  if (window.dataLayer) window.dataLayer.push({ event: name, ...payload });
  if (window.fbq) {
    const m = META_MAP[name];
    if (m) window.fbq("track", m[0], { ...m[1] });
    else window.fbq("trackCustom", name, params);
  }
}
